// TB-109: patient slot picker helpers (date strip, grouped time chips, next available).
// Dates are local calendar days as 'YYYY-MM-DD' strings and are always compared in full.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const pad = (n) => String(n).padStart(2, '0')

export function toIsoDate (d) {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10)
  const date = d instanceof Date ? d : new Date(d)
  if (isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function parseIsoDate (iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

export function addDays (iso, n) {
  const d = parseIsoDate(iso)
  d.setDate(d.getDate() + n)
  return toIsoDate(d)
}

export function sameDay (a, b) {
  if (!a || !b) return false
  return toIsoDate(a) === toIsoDate(b)
}

/** "Wed 22 Oct", with "Today" / "Tomorrow" relative to `today`. */
export function dayLabel (iso, today = toIsoDate(new Date()), { relative = true } = {}) {
  if (!iso) return ''
  if (relative && iso === today) return 'Today'
  if (relative && iso === addDays(today, 1)) return 'Tomorrow'
  const d = parseIsoDate(iso)
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`
}

/** "09:30" from "09:30", "9:30:00" or "09:30:00". */
export function timeLabel (t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''))
  return m ? `${pad(m[1])}:${m[2]}` : ''
}

/** Unique schedule dates from a get-provider-detail or search payload, sorted. */
export function scheduleDates (schedule) {
  const set = new Set()
  ;(Array.isArray(schedule) ? schedule : []).forEach((s) => {
    const iso = s && s.consultationDate ? toIsoDate(s.consultationDate) : ''
    if (iso) set.add(iso)
  })
  return Array.from(set).sort()
}

/**
 * A strip of `days` pills from `startIso`. A day is `available` when the doctor
 * has a schedule on it and it is not known to be full (`fullDays`).
 */
export function buildDateStrip (dates, startIso, days = 14, fullDays = []) {
  const has = new Set(dates || [])
  const full = new Set(fullDays || [])
  const out = []
  for (let i = 0; i < days; i++) {
    const iso = addDays(startIso, i)
    const d = parseIsoDate(iso)
    out.push({
      iso,
      weekday: WEEKDAYS[d.getDay()],
      day: d.getDate(),
      month: MONTHS[d.getMonth()],
      scheduled: has.has(iso),
      full: full.has(iso),
      available: has.has(iso) && !full.has(iso)
    })
  }
  return out
}

/** Minutes since midnight of a "HH:mm" time. */
export function minutesOf (t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''))
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN
}

/** Free and still in the future (a booked slot is never offered). */
export function isSlotBookable (slot, now = new Date()) {
  if (!slot || slot.status === false) return false
  const iso = toIsoDate(slot.consultationDate)
  const mins = minutesOf(slot.startTime)
  if (!iso || !Number.isFinite(mins)) return false
  const start = parseIsoDate(iso)
  start.setMinutes(mins)
  return start.getTime() > now.getTime()
}

// TB-178: 00:00 to 05:59 is its own "Night" group so those slots are never dropped
export const PERIODS = [
  { key: 'night', label: 'Night' },
  { key: 'morning', label: 'Morning' },
  { key: 'afternoon', label: 'Afternoon' },
  { key: 'evening', label: 'Evening' }
]

export function periodOf (startTime) {
  const mins = minutesOf(startTime)
  if (mins < 6 * 60) return 'night'
  if (mins < 12 * 60) return 'morning'
  if (mins < 17 * 60) return 'afternoon'
  return 'evening'
}

/** Bookable slots sorted by start time and grouped Night / Morning / Afternoon / Evening; empty groups dropped. */
export function groupSlots (slots, now = new Date()) {
  const sorted = (Array.isArray(slots) ? slots : [])
    .filter((s) => isSlotBookable(s, now))
    .sort((a, b) => minutesOf(a.startTime) - minutesOf(b.startTime))
  return PERIODS.map((p) => ({ ...p, slots: sorted.filter((s) => periodOf(s.startTime) === p.key) }))
    .filter((g) => g.slots.length)
}

/** The schedule API answers 400 "No available slots." for a day with nothing free. */
export function isNoSlotsError (err) {
  const res = err && err.response
  if (!res || res.status !== 400) return false
  const msg = String((res.data && res.data.message) || '')
  return /no available slots/i.test(msg)
}

/** Consultation types present in a list of slot rows, in first-seen order. */
export function slotTypes (slots) {
  const seen = []
  ;(Array.isArray(slots) ? slots : []).forEach((s) => {
    if (s && s.type && !seen.includes(s.type)) seen.push(s.type)
  })
  return seen
}

/**
 * Per-doctor cache of slot lists by date, so a date is fetched once.
 * `fetcher(iso)` resolves to an array (empty for a full day).
 */
export function createSlotCache (fetcher) {
  const byDate = new Map()
  return {
    get (iso) {
      if (!byDate.has(iso)) {
        const p = Promise.resolve()
          .then(() => fetcher(iso))
          .then((list) => (Array.isArray(list) ? list : []))
          .catch((e) => {
            byDate.delete(iso)
            throw e
          })
        byDate.set(iso, p)
      }
      return byDate.get(iso)
    },
    clear () { byDate.clear() }
  }
}

/**
 * The first date (in order, from `fromIso`) with a bookable slot.
 * Resolves { date, slots, checked: [isoDates that were full] } or null.
 */
export async function findNextAvailable (dates, getSlots, { fromIso, now = new Date(), maxDays = 10 } = {}) {
  const start = fromIso || toIsoDate(now)
  const list = (dates || []).filter((d) => d >= start).slice(0, maxDays)
  const full = []
  for (const iso of list) {
    let slots = []
    try {
      slots = await getSlots(iso)
    } catch (e) {
      if (!isNoSlotsError(e)) throw e
    }
    const free = groupSlots(slots, now)
    if (free.length) return { date: iso, slots, full }
    full.push(iso)
  }
  return full.length ? { date: null, slots: [], full } : null
}

/** A queue that runs at most `limit` jobs at once; `run(fn)` resolves with fn's result. */
export function createLimiter (limit = 4) {
  let active = 0
  const queue = []
  const pump = () => {
    while (active < limit && queue.length) {
      const { fn, resolve, reject } = queue.shift()
      active++
      Promise.resolve()
        .then(fn)
        .then(resolve, reject)
        .finally(() => {
          active--
          pump()
        })
    }
  }
  return {
    run (fn) {
      return new Promise((resolve, reject) => {
        queue.push({ fn, resolve, reject })
        pump()
      })
    },
    clear () { queue.length = 0 }
  }
}
